// Report-showcase carousel. Declarative: every [data-carousel] element on
// the page becomes a slideshow. No framework. Behaviour:
//  - translateX track, prev/next arrows wrap around
//  - dots generated from slide count, reflect + drive the active slide
//  - pointer swipe (>40px) flips a slide
//  - auto-rotation (data-carousel-interval ms, default 6500), paused on any
//    interaction and resumed only after 15s of idle
//  - prefers-reduced-motion: never auto-start
//  - tab hidden (visibilitychange): pause; restored on return

const RESUME_IDLE_MS = 15000;
const SWIPE_THRESHOLD = 40;

function setupCarousel(el: HTMLElement): void {
  const track = el.querySelector<HTMLElement>(".carousel-track");
  const slides = Array.from(el.querySelectorAll<HTMLElement>(".carousel-slide"));
  const dotsBox = el.querySelector<HTMLElement>(".carousel-dots");
  if (!track || slides.length < 2) return;

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const interval = Number(el.dataset.carouselInterval) || 6500;

  let index = 0;
  let rotate: number | undefined;
  let resume: number | undefined;

  const dots: HTMLButtonElement[] = slides.map((_, i) => {
    const dot = document.createElement("button");
    dot.type = "button";
    dot.setAttribute("aria-label", `Go to report section ${i + 1}`);
    dot.addEventListener("click", () => {
      interrupt();
      goTo(i);
    });
    dotsBox?.appendChild(dot);
    return dot;
  });

  function goTo(i: number): void {
    index = (i + slides.length) % slides.length;
    track!.style.transform = `translateX(-${index * 100}%)`;
    dots.forEach((dot, di) =>
      dot.setAttribute("aria-current", di === index ? "true" : "false"),
    );
  }

  function start(): void {
    if (reduce || document.hidden || rotate !== undefined) return;
    rotate = window.setInterval(() => goTo(index + 1), interval);
  }

  function stop(): void {
    if (rotate !== undefined) window.clearInterval(rotate);
    rotate = undefined;
  }

  // Any interaction stops rotation and schedules a single resume after idle.
  function interrupt(): void {
    stop();
    if (resume !== undefined) window.clearTimeout(resume);
    resume = window.setTimeout(start, RESUME_IDLE_MS);
  }

  el.querySelector(".carousel-prev")?.addEventListener("click", () => {
    interrupt();
    goTo(index - 1);
  });
  el.querySelector(".carousel-next")?.addEventListener("click", () => {
    interrupt();
    goTo(index + 1);
  });

  let downX: number | null = null;
  el.addEventListener("pointerdown", (e) => (downX = e.clientX));
  el.addEventListener("pointerup", (e) => {
    if (downX === null) return;
    const dx = e.clientX - downX;
    downX = null;
    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      interrupt();
      goTo(index + (dx < 0 ? 1 : -1));
    }
  });

  el.addEventListener("focusin", interrupt);
  slides.forEach((s) => s.addEventListener("scroll", interrupt, true));

  document.addEventListener("visibilitychange", () =>
    document.hidden ? stop() : start(),
  );

  goTo(0);
  start();
}

export function initCarousels(): void {
  document
    .querySelectorAll<HTMLElement>("[data-carousel]")
    .forEach(setupCarousel);
}
